import qs from 'qs';
import AbstractApiModel from '@/models/abstracts/AbstractApiModel';
import PeriodModel from '@/models/entities/PeriodModel';
import { listPage } from '@/models/listPage';
import { TIME_SHEET_STATUS } from '@/models/references/references';
import type { PageRequest } from '@/models/listPage';
import type { ReferenceOption } from '@/stores/useReferenceOptions';

/** Chiffres des feuilles d'un statut (GET /time_sheets/_aggregated_data) */
export interface StatusFigures {
  /** Id du statut (TimeSheetStatus) */
  timeSheetStatus: number;
  count: number;
  totalMinutes: number;
}

/** Tuiles du Tableau de bord, dans les unités de l'écran (demandes, minutes) */
export interface DashboardTiles {
  pending: { count: number; director: number; manager: number };
  pendingMinutes: { total: number; director: number; manager: number };
  done: { minutes: number; count: number };
}

/** GET /time_sheets/_aggregated_data : chiffres par statut des feuilles que filtre la liste (mêmes droits) */
class TimeSheetAggregatedDataModel extends AbstractApiModel {
  protected _path = '/time_sheets/_aggregated_data';
}

/** Dernière période de la société (la période en cours : une seule ouverte, la plus récente) */
export async function loadLatestPeriod(companyId: number): Promise<PeriodModel | null> {
  const { models } = await listPage(PeriodModel, {
    filters: { company: companyId },
    order: { month: 'desc' },
    page: 1,
    itemsPerPage: 1,
    partial: true,
  });

  return models[0] ?? null;
}

/** Filtres de l'agrégat : la période, et les équipes du manager (null : le Dirigeant, toute la société) */
export function aggregatedFilters(periodId: number, teamIds: number[] | null): Record<string, number | number[]> {
  return teamIds ? { period: periodId, 'teamMember.team': teamIds } : { period: periodId };
}

export async function loadStatusFigures(periodId: number, teamIds: number[] | null): Promise<StatusFigures[]> {
  const model = new TimeSheetAggregatedDataModel();
  const query = qs.stringify(aggregatedFilters(periodId, teamIds), { arrayFormat: 'brackets', encodeValuesOnly: true });
  const { data } = await model.connector.fetch(`${model.fullPath}?${query}`, { silentError: true });

  return ((data ?? {}) as { byStatus?: StatusFigures[] }).byStatus ?? [];
}

/**
 * Tuiles à partir des chiffres par statut : demandes et heures en attente (du Dirigeant, du manager), heures traitées
 * (feuilles validées) et leur nombre de demandes. statuses : la liste de référence (ids de l'API)
 */
export function dashboardTiles(figures: StatusFigures[], statuses: ReferenceOption[]): DashboardTiles {
  const of = (reference: string) => {
    const id = statuses.find((status) => status.reference === reference)?.id;
    return figures.find((figure) => figure.timeSheetStatus === id) ?? { count: 0, totalMinutes: 0 };
  };
  const director = of(TIME_SHEET_STATUS.PENDING_DIRECTOR);
  const manager = of(TIME_SHEET_STATUS.PENDING_MANAGER);
  const validated = of(TIME_SHEET_STATUS.VALIDATED);

  return {
    pending: { count: director.count + manager.count, director: director.count, manager: manager.count },
    pendingMinutes: {
      total: director.totalMinutes + manager.totalMinutes,
      director: director.totalMinutes,
      manager: manager.totalMinutes,
    },
    done: { minutes: validated.totalMinutes, count: validated.count },
  };
}

/** Collaborateurs par page de la répartition */
export const BREAKDOWN_PAGE_SIZE = 10;

/**
 * Répartition par collaborateur : une feuille par collaborateur sur la période, soumises (tout sauf brouillon : en
 * attente ou validées), absences exclues (0h), par heures décroissantes puis NOM Prénom, 10 par page ; le manager :
 * ses équipes. null tant que les listes de référence ne sont pas chargées.
 */
export function breakdownRequest(
  periodId: number,
  teamIds: number[] | null,
  draftStatusId: number | null,
  normalTypeId: number | null,
  page: number,
): PageRequest | null {
  if (draftStatusId === null || normalTypeId === null) return null;

  return {
    filters: {
      ...aggregatedFilters(periodId, teamIds),
      'not[timeSheetStatus]': [draftStatusId],
      timeSheetType: normalTypeId,
    },
    order: { 'timeSheetSummaries.totalMinutes': 'desc', 'member.user.lastName': 'asc', 'member.user.firstName': 'asc' },
    page,
    itemsPerPage: BREAKDOWN_PAGE_SIZE,
  };
}

/** Hauteur d'une colonne (%), à l'échelle du plus gros total de la période (le premier de la page 1) */
export function columnHeight(minutes: number, maxMinutes: number): number {
  return maxMinutes > 0 ? Math.round((minutes / maxMinutes) * 1000) / 10 : 0;
}

/** Pas possibles des graduations, en minutes : 30 min, 1h, 2h, 5h, 10h, 20h, 50h, 100h */
const TICK_STEPS = [30, 60, 120, 300, 600, 1200, 3000, 6000];

/**
 * Graduations de l'échelle des heures (minutes) : de 0 au premier pas rond qui dépasse le plus gros total, 5 au plus ;
 * la dernière est le haut de l'échelle (100 % d'une colonne)
 */
export function hourTicks(maxMinutes: number): number[] {
  const step = TICK_STEPS.find((candidate) => Math.ceil(maxMinutes / candidate) <= 4) ?? TICK_STEPS.at(-1)!;
  const count = Math.max(1, Math.ceil(maxMinutes / step));

  return Array.from({ length: count + 1 }, (_, i) => i * step);
}
